import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import FontIcon from "react-md/lib/FontIcons";
import EventService from "service/event/EventService";
import AuthenticationService from "service/auth/AuthenticationService";
import LTRService from "../service/LTRService";
import "../styles/ltrCall.scss";

const ltrService = new LTRService();
const authenticationService = new AuthenticationService();

const call = (fn, ...args) =>
  new Promise((resolve, reject) => fn(...args, resolve, reject));

// "+91 7658X XXXXX": the panel shows enough to pick the right number without
// putting the whole number on screen.
function maskNumber(countryCode, phoneNo) {
  const digits = String(phoneNo || "").replace(/\D/g, "");
  if (!digits) return "-";
  const shown = digits.slice(0, Math.min(4, digits.length));
  const hidden = Math.max(digits.length - shown.length, 0);
  const tail = hidden > 0 ? "X" + (hidden > 1 ? " " + "X".repeat(hidden - 1) : "") : "";
  return `${countryCode ? `+${countryCode} ` : ""}${shown}${tail}`;
}

/**
 * Call details (board 06.1): the patient's primary and alternate numbers, each
 * dialled through SparkTG. Placing a call opens it on the LTR case and hands
 * the case back through onCallPlaced, which starts the in-call pill.
 */
function CallDetailsDialog({ open, patient, caseRecord, onClose, onCallPlaced }) {
  const [alternates, setAlternates] = useState([]);
  const [agent, setAgent] = useState(undefined); // undefined = loading, null = none
  const [dialling, setDialling] = useState("");

  const employeeId = authenticationService.getUserProfile()?.employeeProfile?.id;

  useEffect(() => {
    if (!open || !patient) return;
    setDialling("");
    call(ltrService.fetchAlternatePhoneNos, patient.id)
      .then((res) => setAlternates(Array.isArray(res) ? res : []))
      .catch(() => setAlternates([]));
    if (!employeeId) {
      setAgent(null);
      return;
    }
    setAgent(undefined);
    call(ltrService.fetchSparktgAgent, employeeId)
      .then((res) => setAgent(res && res.callingNumber ? res : null))
      .catch(() => setAgent(null));
  }, [open, patient, employeeId]);

  if (!patient) return null;

  const numbers = [
    {
      key: "primary",
      phoneNo: patient.phoneno,
      countryCode: patient.countryCode,
      primary: true,
    },
    ...alternates
      .filter((a) => a && a.alternateNumber)
      .map((a, i) => ({
        key: `alt-${i}`,
        phoneNo: a.alternateNumber,
        countryCode: a.countryCode,
        description: a.description,
      })),
  ];

  const place = (n) => {
    // The SparkTG call API takes the number as an integer; alternate numbers
    // can arrive as strings, so keep only the digits.
    const phoneNo = Number(String(n.phoneNo || "").replace(/\D/g, ""));
    if (dialling || !agent || !phoneNo) return;
    setDialling(n.key);
    call(ltrService.callPatient, {
      employeeId,
      patientId: patient.id,
      phoneNo,
    })
      .then(() => call(ltrService.logCall, caseRecord.id))
      .then((updated) => {
        setDialling("");
        onCallPlaced(updated);
      })
      .catch((err) => {
        setDialling("");
        console.error("Error placing the call:", err);
        EventService.emit("showError", {
          message: err?.message || "Could not place the call. Please try again.",
        });
      });
  };

  const callButton = (n) => (
    <button
      type="button"
      className="ltr-call-btn"
      disabled={!agent || !!dialling || !caseRecord}
      onClick={() => place(n)}
    >
      <FontIcon>call</FontIcon>
      {dialling === n.key ? "Calling..." : "Call"}
    </button>
  );

  const [primary, ...rest] = numbers;

  return (
    <Dialog
      open={open}
      onClose={() => !dialling && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: "ltr-call-details" }}
    >
      <div className="ltr-call-details-head">
        <h3>Call details</h3>
        <button
          type="button"
          className="ltr-call-details-close"
          onClick={() => !dialling && onClose()}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <p className="ltr-call-details-label">Primary number</p>
      <div className="ltr-call-details-primary">
        <span className="ltr-call-number">
          {maskNumber(primary.countryCode, primary.phoneNo)}
        </span>
        {callButton(primary)}
      </div>

      {rest.length > 0 && (
        <>
          <p className="ltr-call-details-sub">Alternate numbers provided by patient</p>
          {rest.map((n) => (
            <div key={n.key} className="ltr-call-row">
              <div>
                <div className="ltr-call-number">{maskNumber(n.countryCode, n.phoneNo)}</div>
                {n.description && <div className="ltr-call-desc">{n.description}</div>}
              </div>
              {callButton(n)}
            </div>
          ))}
        </>
      )}

      {agent === null && (
        <p className="ltr-call-details-note error">
          No SparkTG calling number is set up for you, so calls cannot be placed. Ask an
          admin to add you as a SparkTG agent.
        </p>
      )}
    </Dialog>
  );
}

export default CallDetailsDialog;
